import React from "react";
import HeroSection from "../components/HeroSection";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const rcmSolutions = [
  {
    title: "Medical Billing",
    description:
      "Ensure accurate and timely medical billing with our end-to-end billing services designed to improve reimbursement rates and minimize revenue leakage.",
    image: images.rcmServiceSolutions.medicalBilling,
    serviceTitle: "Our Medical Billing Services Include:",
    services: [
      "Patient Demographic Entry",
      "Charge Entry",
      "Medical Coding Support",
      "Billing Submission",
      "Payment Posting",
      "Billing Audits",
      "Compliance Monitoring",
      "Revenue Reporting",
    ],
    benefits: [
      "Accurate Billing Process",
      "Faster Reimbursements",
      "Reduced Billing Errors",
      "Improved Cash Flow",
      "Increased Revenue Collection",
    ],
  },
  {
    title: "Claims Processing",
    description:
      "Accelerate claim submissions and reimbursements with efficient claims management that reduces delays and improves first-pass claim acceptance.",
    image: images.rcmServiceSolutions.claimsProcessing,
    serviceTitle: "Our Claims Processing Services Include:",
    services: [
      "Electronic Claim Submission",
      "Claim Validation",
      "Claim Tracking",
      "Claims Follow-Up",
      "Insurance Coordination",
      "Payment Reconciliation",
      "Claims Status Reporting",
      "Compliance Checks",
    ],
    benefits: [
      "Faster Claim Processing",
      "Higher First-Pass Acceptance Rate",
      "Reduced Claim Rejections",
      "Improved Revenue Cycle",
      "Enhanced Operational Efficiency",
    ],
  },
  {
    title: "Release of Information (ROI)",
    description:
      "Manage patient health information securely while ensuring compliance with healthcare regulations and timely processing of information requests.",
    image: images.rcmServiceSolutions.releaseOfInformation,
    serviceTitle: "Our Release of Information Services Include:",
    services: [
      "Medical Record Requests",
      "Patient Authorization Verification",
      "Secure Document Processing",
      "Electronic Record Delivery",
      "Compliance Management",
      "Audit Support",
      "Request Tracking",
      "Quality Assurance",
    ],
    benefits: [
      "Secure Information Handling",
      "Faster Request Processing",
      "Regulatory Compliance",
      "Improved Patient Satisfaction",
      "Reduced Administrative Burden",
    ],
  },
  {
    title: "Eligibility Verification",
    description:
      "Verify patient insurance coverage before services are provided to reduce billing issues and improve payment accuracy.",
    image: images.rcmServiceSolutions.eligibilityVerification,
    serviceTitle: "Our Eligibility Verification Services Include:",
    services: [
      "Insurance Coverage Verification",
      "Benefits Verification",
      "Copay & Deductible Validation",
      "Prior Authorization Support",
      "Patient Eligibility Confirmation",
      "Coverage Updates",
      "Verification Reporting",
      "Appointment Readiness Support",
    ],
    benefits: [
      "Reduced Claim Denials",
      "Improved Payment Accuracy",
      "Better Patient Experience",
      "Faster Registration Process",
      "Increased Revenue Collection",
    ],
  },
  {
    title: "Denial Management",
    description:
      "Identify, analyze, and resolve claim denials efficiently to recover lost revenue and improve reimbursement performance.",
    image: images.rcmServiceSolutions.denialManagement,
    serviceTitle: "Our Denial Management Services Include:",
    services: [
      "Denial Analysis",
      "Root Cause Identification",
      "Appeals Management",
      "Claim Corrections",
      "Resubmission Support",
      "Denial Trend Reporting",
      "Process Improvement Recommendations",
      "Performance Monitoring",
    ],
    benefits: [
      "Lower Denial Rates",
      "Increased Revenue Recovery",
      "Faster Claim Resolution",
      "Improved Billing Accuracy",
      "Enhanced Financial Performance",
    ],
  },
  {
    title: "Accounts Receivable (AR) Management",
    description:
      "Improve cash flow by efficiently managing outstanding balances and accelerating collections.",
    image: images.rcmServiceSolutions.accountsReceivableManagement,
    serviceTitle: "Our AR Management Services Include:",
    services: [
      "AR Follow-Up",
      "Insurance Collections",
      "Patient Collections Support",
      "Aging Analysis",
      "Payment Reconciliation",
      "Outstanding Balance Resolution",
      "Revenue Reporting",
      "Collection Performance Monitoring",
    ],
    benefits: [
      "Reduced Outstanding Receivables",
      "Improved Cash Flow",
      "Faster Payment Collection",
      "Higher Collection Rates",
      "Better Financial Stability",
    ],
  },
];

const processSteps = [
  "Patient Registration & Insurance Verification",
  "Medical Documentation Review",
  "Charge Entry & Medical Billing",
  "Claims Submission",
  "Claims Tracking & Follow-Up",
  "Payment Posting",
  "Denial Analysis & Appeals",
  "Accounts Receivable Follow-Up",
  "Revenue Reporting & Performance Optimization",
];

const reasons = [
  "Experienced RCM Professionals",
  "HIPAA-Compliant Processes",
  "Accurate Billing & Claims Management",
  "Reduced Claim Denials",
  "Faster Reimbursement Cycles",
  "Dedicated Quality Assurance",
  "Transparent Reporting & Analytics",
  "Scalable Service Models",
  "Cost-Effective RCM Solutions",
  "Commitment to Operational Excellence",
];

const healthcareSegments = [
  "Hospitals",
  "Multi-Specialty Clinics",
  "Physician Practices",
  "Diagnostic Centers",
  "Ambulatory Surgery Centers (ASCs)",
  "Urgent Care Centers",
  "Dental Clinics",
  "Behavioral Health Providers",
  "Telemedicine Providers",
  "Medical Billing Companies",
];

const ListGroup = ({ title, items }) => (
  <div>
    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
      {title}
    </h4>
    <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const RCMServices = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.rcmServices} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">
            Revenue Cycle Management (RCM) Services
          </p>
          <h2 className="heading-main mb-5">
            Streamline Your Revenue Cycle with Efficient RCM Solutions
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              Managing the financial health of a healthcare organization
              requires a well-structured revenue cycle. At Westlisoft, we
              provide comprehensive Revenue Cycle Management (RCM) services
              that help healthcare providers improve cash flow, reduce claim
              denials, enhance operational efficiency, and maximize revenue.
            </p>
            <p className="text-body">
              Our experienced RCM professionals leverage industry best
              practices and technology-driven processes to ensure accurate
              billing, faster reimbursements, and regulatory compliance. Whether
              you are a hospital, clinic, physician practice, diagnostic center,
              or healthcare organization, our customized RCM solutions are
              designed to optimize every stage of your revenue cycle.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer p-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our RCM Solutions
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {rcmSolutions.map((solution, index) => {
              const imageFirst = index % 2 === 1;

              return (
                <article
                  key={solution.title}
                  className="grid items-center gap-6 rounded-card bg-white p-5 shadow-sm md:grid-cols-2 md:gap-10 lg:p-8"
                >
                  <div
                    className={
                      imageFirst
                        ? "order-2 md:order-2"
                        : "order-2 md:order-1"
                    }
                  >
                    <h3 className="mb-4 font-rubik text-2xl font-bold text-secondary md:text-3xl">
                      {solution.title}
                    </h3>
                    <p className="text-body mb-5">{solution.description}</p>
                    <div className="space-y-6">
                      <ListGroup
                        title={solution.serviceTitle}
                        items={solution.services}
                      />
                      <ListGroup title="Benefits:" items={solution.benefits} />
                    </div>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-1 md:h-[320px] lg:h-[360px]"
                        : "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-2 md:h-[320px] lg:h-[360px]"
                    }
                  >
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">Our RCM Process</p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We follow a structured approach to optimize every stage of the
            revenue cycle.
          </p>
          <ol className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {processSteps.map((step, index) => (
              <li
                key={step}
                className="flex min-h-24 flex-col items-center justify-center rounded-card bg-background px-4 py-5 text-center font-rubik text-sm font-light text-gray-700"
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary font-bold text-secondary">
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <h2 className="heading-main mb-8 text-primary">
            Why Choose Westlisoft?
          </h2>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex min-h-20 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Healthcare Segments We Serve
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            Our Revenue Cycle Management services support a wide range of
            healthcare organizations, including:
          </p>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {healthcareSegments.map((segment) => (
              <li
                key={segment}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {segment}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Maximize Revenue. Minimize Administrative Burden.
          </h2>
          <div className="mx-auto max-w-4xl space-y-4">
            <p className="text-body">
              At Westlisoft, we help healthcare organizations strengthen their
              financial performance through efficient, compliant, and
              technology-driven Revenue Cycle Management services. By optimizing
              every stage of the revenue cycle, we enable providers to focus
              more on delivering exceptional patient care while we handle the
              complexities of revenue management.
            </p>
            <p className="text-body">
              Partner with Westlisoft to improve operational efficiency,
              accelerate reimbursements, and achieve sustainable financial
              growth.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RCMServices;
